import React, { useEffect, useState, useCallback } from "react";
import { Button, message, Card } from "antd";
import { EyeOutlined } from "@ant-design/icons";
import { useTranslation } from "react-i18next";
import CustomTableComponents from "../CommonComponents/CustomTableComponents";
import { useDispatch, useSelector } from "react-redux";
import {
  getFilteredTransactions,
  getReprintLogs,
  getSummaryTransactions,
} from "../../redux/transactionSlice";
import TicketCardModal from "./TicketCardModal";
import ReprintLogsModal from "./ReprintLogsModal";
import { formatDate } from "../../Utils/Constants/utils";
import Header from "../../global/Header/Header";
import { formatDateAndTime } from "../../Utils/Imports/date-utils";
import dayjs from "dayjs";
import isBetween from "dayjs/plugin/isBetween";
import { getAllKiosks } from "../../redux/kiosksSlice";
import { getBundles } from "../../redux/bundleSlice";
import { MinusOutlined } from "@ant-design/icons";
import { getAllUsersByRole } from "../../redux/userSlice";
import TransactionFilterDrawer from "./TransactionFilter";
import SummaryModal from "./SummaryModal";
import { useLocation } from "react-router-dom";
import { style } from "../../Styles";
import TransactionPaymentStatus from "./TransactionPaymentStatus";
import PaymentDetailsModal from "./PaymentDetailsModal/PaymentDetailsModal";
import { getTicketsByTransactionId } from "../../redux/ticketSlice";
import { getCardByTransactionId } from "../../redux/cardSlice";
import debounce from "lodash.debounce";

const today = dayjs().startOf("day");

const Transaction = () => {
  const { t } = useTranslation();
  dayjs.extend(isBetween);

  const [csvData, setCsvData] = useState([]);

  const dispatch = useDispatch();
  const location = useLocation();
  const { transactions } = useSelector((state) => state.transactions);
  const { kiosks } = useSelector((state) => state.kiosks);
  const { users } = useSelector((state) => state.users);
  const { reprintLogs } = useSelector((state) => state.transactions);

  const columns = [
    {
      title: "#",
      key: "index",
      render: (text, record, index) => <span>{index + 1}</span>,
      show: true,
      width: 50,
    },
    {
      title: t("transactionScreen.table.kiosk"),
      key: "kiosk",
      render: (record) => {
        return <span>{record.kioskName}</span>;
      },
      sorter: (a, b) => a.kioskName - b.kioskName,
      show: true,
      width: 120,
    },
    {
      title: t("transactionScreen.table.transactionType"),
      key: "type",
      render: (record) => {
        return (
          <span>
            {record.type === "TICKET_TYPE" ? (
              <div>{t("transactionScreen.transactionTypes.ticket")}</div>
            ) : record.type === "NEW_CARD_TYPE" ? (
              <div>{t("transactionScreen.transactionTypes.newCard")}</div>
            ) : record.type === "TOP_UP_CARD_TYPE" ? (
              <div>{t("transactionScreen.transactionTypes.topupCard")}</div>
            ) : record.type === "SPECIAL_CARD_TYPE" ? (
              <div>{t("transactionScreen.transactionTypes.specialCard")}</div>
            ) : record.type === "CARD_REPLACEMENT_FEE" ? (
              <div>
                {t("transactionScreen.transactionTypes.cardReplacement")}
              </div>
            ) : (
              <div>-</div>
            )}
          </span>
        );
      },
      sorter: (a, b) => a.type.length - b.type.length,
      show: true,
      width: 130,
    },
    {
      title: t("transactionScreen.table.paymentType"),
      key: "paymentType",
      render: (record) => {
        return <span>{record.paymentType}</span>;
      },
      sorter: (a, b) => a.paymentType.length - b.paymentType.length,
      show: true,
      width: 100,
    },
    {
      title: t("transactionScreen.table.totalAmount"),
      key: "totalAmount",
      render: (record) => {
        return <span>{`€ ${record.totalAmount}`}</span>;
      },
      sorter: (a, b) => a.totalAmount.length - b.totalAmount.length,
      show: true,
      width: 100,
    },
    {
      title: t("transactionScreen.table.bundle"),
      key: "bundle",
      render: (record) => {
        return <span>{record.bundleName}</span>;
      },
      sorter: (a, b) => a.bundleName.length - b.bundleName.length,
      show: true,
      width: 120,
    },
    {
      title: t("transactionScreen.table.tickets"),
      key: "ticketCount",
      render: (record) => {
        return record?.ticketCount ? (
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => showModal(record?.id, "ticket")}
          >
            {`${record?.ticketCount}`}
          </Button>
        ) : (
          <div style={{ textAlign: "center" }}> _ </div>
        );
      },
      show: true,
      width: 80,
    },
    {
      title: t("transactionScreen.table.cards"),
      key: "cardCount",
      render: (record) => {
        return record?.cardCount > 0 ? (
          <Button
            icon={<EyeOutlined />}
            size="small"
            onClick={() => showModal(record?.id, "card")}
          >
            {`${record?.cardCount}`}
          </Button>
        ) : (
          <div style={{ textAlign: "center" }}> _ </div>
        );
      },
      show: true,
      width: 80,
    },
    {
      title: t("transactionScreen.table.date"),
      key: "created",
      render: (record) => {
        return <span>{formatDateAndTime(record.created)}</span>;
      },
      sorter: (a, b) => a.created.length - b.created.length,
      show: true,
      width: 160,
    },
    {
      title: t("transactionScreen.table.driver"),
      key: "driver",
      render: (record) => {
        return (
          <span>
            {record?.driverName ? (
              `${record?.driverName} ${record?.driverSurname}`
            ) : (
              <MinusOutlined />
            )}
          </span>
        );
      },
      sorter: (a, b) => a?.driverName - b?.driverName,
      show: true,
      width: 130,
    },
    {
      title: t("transactionScreen.table.reprinted"),
      key: "reprinted",
      render: (record) => {
        // Only show reprint status for TICKET_TYPE transactions
        if (record.type !== "TICKET_TYPE") {
          return <MinusOutlined />;
        }

        if (!record.reprinted) {
          return <MinusOutlined />;
        } // Determine style based on reprint count from transaction data
        const reprintCount = record.reprintCount || 1;
        const statusStyle =
          reprintCount > 1 ? style.statusDanger : style.statusWarning;
        const statusText =
          reprintCount > 1
            ? t("statuses.reprintedMultiple")
            : t("statuses.reprinted");

        return (
          <span style={style.dataContainer}>
            <div
              style={statusStyle}
              onClick={() => showReprintLogsModal(record)}
            >
              {statusText}
            </div>
          </span>
        );
      },
      sorter: (a, b) => a.reprinted - b.reprinted,
      show: true,
      width: 100,
    },
    {
      title: t("transactionScreen.table.paymentStatus"),
      key: "paymentStatus",
      render: (record) => {
        return (
          <span style={style.dataContainer}>
            {record.paymentStatus === "PAID" ? (
              <div style={style.statusActif}>{t("statuses.paid")}</div>
            ) : (
              <div style={style.statusInactif}>{t("statuses.notPaid")}</div>
            )}
          </span>
        );
      },
      sorter: (a, b) => a.paymentStatus - b.paymentStatus,
      show: true,
      width: 90,
    },
    {
      title: "Payment Status Updated",
      key: "paymentStatusUpdated",
      render: (record) => {
        return (
          <span>
            {record.paymentStatusUpdatedAt ? (
              formatDateAndTime(record.paymentStatusUpdatedAt)
            ) : (
              <MinusOutlined />
            )}
          </span>
        );
      },
      sorter: (a, b) => {
        const dateA = a.paymentStatusUpdatedAt
          ? new Date(a.paymentStatusUpdatedAt)
          : new Date(0);
        const dateB = b.paymentStatusUpdatedAt
          ? new Date(b.paymentStatusUpdatedAt)
          : new Date(0);
        return dateA.getTime() - dateB.getTime();
      },
      show: true,
      width: 160,
    },
    ...(transactions?.data?.some((record) => record.payment || record.handheldPayment)
      ? [
          {
            title: t("transactionScreen.table.paymentDetails"),
            key: "paymentDetails",
            render: (record) => {
              if (record.payment || record.handheldPayment) {
                return (
                  <Button
                    icon={<EyeOutlined />}
                    onClick={() => showPaymentDetailsModal(record)}
                  >
                    {t("transactionScreen.table.viewDetails")}
                  </Button>
                );
              }
              return null; // Hide the button if neither is found
            },
            show: true,
            width: 150,
          },
        ]
      : []),
  ];

  const [filter, setFilter] = useState("");
  const [dateFilter, setDateFilter] = useState([today, today]);
  const [isMakingApiCall, setMakingApiCall] = useState(false);
  const [isModalOpen, setModalOpen] = useState(false);
  const [modalSummary, setModalSummary] = useState(false);
  const [totalSummaryValues, setTotalSummaryValues] = useState(null);
  const [data, setData] = useState([]);
  const [type, setType] = useState(null);

  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [selectedKiosk, setSelectedKiosk] = useState(null);
  const [selectedDriver, setSelectedDriver] = useState(null);
  const [selectedType, setSelectedType] = useState(null);
  const [selectedPaymentType, setSelectedPaymentType] = useState(null);
  const [selectedKioskType, setSelectedKioskType] = useState(null);
  const [selectedPaymentStatus, setSelectedPaymentStatus] = useState(null);
  const [selectedReprintedStatus, setSelectedReprintedStatus] = useState(null);
  const [selectedBundle, setSelectedBundle] = useState(null);
  const [openChangePaymentStatusModal, setOpenChangePaymentStatusModal] =
    useState(false);
  const [isReprintLogsModalOpen, setIsReprintLogsModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState(null);
  const [selectedTransactionForReprint, setSelectedTransactionForReprint] =
    useState(null);
  const [isPaymentDetailsModalOpen, setPaymentDetailsModalOpen] =
    useState(false);
  const [paymentDetailsData, setPaymentDetailsData] = useState(null);

  const onResetFilter = () => {
    setSelectedKiosk(null);
    setSelectedDriver(null);
    setSelectedType(null);
    setSelectedPaymentType(null);
    setSelectedKioskType(null);
    setSelectedPaymentStatus(null);
    setSelectedReprintedStatus(null);
    setSelectedBundle(null);
    //setDateFilter([today, tomorrow]);
    // close the drawer
    setSelectedOptionDropdownFields({
      type: null,
      paymentType: null,
      kioskId: null,
      driverId: null,
      paymentStatus: null,
      reprintedStatus: null,
      bundleId: null,
    });
    setIsDrawerOpen(false);
  };

  const closeFilteringDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onDateRangeChange = (value) => {
    setDateFilter(value);
  };
  const [selectedOptionDropdownFields, setSelectedOptionDropdownFields] =
    useState({
      type: null,
      paymentType: null,
      kioskId: null,
      driverId: null,
      paymentStatus: null,
      reprintedStatus: null,
    });
  const [csvHeaders, setCsvHeaders] = useState([
    {
      label: "#",
      key: "index",
    },
    {
      label: t("transactionScreen.table.kiosk"),
      key: "kioskName",
    },
    {
      label: t("transactionScreen.table.kioskSerialNumber"),
      key: "kioskSerialNumber",
    },
    {
      label: t("transactionScreen.table.transactionType"),
      key: "type",
    },
    {
      label: t("transactionScreen.table.paymentType"),
      key: "paymentType",
    },
    {
      label: t("transactionScreen.table.totalAmount"),
      key: "totalAmount",
    },
    {
      label: t("transactionScreen.table.bundle"),
      key: "bundleName",
    },
    {
      label: t("transactionScreen.table.tickets"),
      key: "ticketCount",
    },
    {
      label: t("transactionScreen.table.cards"),
      key: "cardCount",
    },
    {
      label: t("transactionScreen.table.date"),
      key: "created",
    },
    {
      label: t("transactionScreen.table.driver"),
      key: "driver",
    },
    {
      label: t("transactionScreen.table.paymentStatus"),
      key: "paymentStatus",
    },
  ]);

  const fetchDrivers = async () => {
    setMakingApiCall(true);
    dispatch(getAllUsersByRole({ role: "DRIVER_ROLE" }))
      .unwrap()
      .then(() => {})
      .catch((err) => {
        message.error("Error while fetching drivers");
      })
      .finally(() => setMakingApiCall(false));
  };

  const fetchKiosks = async () => {
    setMakingApiCall(true);
    dispatch(getAllKiosks())
      .unwrap()
      .then(() => {})
      .catch((err) => {
        message.error("Error while fetching kiosks");
      })
      .finally(() => setMakingApiCall(false));
  };

  const fetchBundles = () => {
    dispatch(getBundles());
  };

  useEffect(() => {
    fetchKiosks();
    fetchDrivers();
    fetchBundles();
  }, [dispatch]);

  //With this object you can pass to filter dropdown list a list of simple values(string or number) or either a list of object
  const dropdownFieldsToShowInFilter = [
    {
      label: t("transactionScreen.filterDrawer.kiosks"),
      key: "kioskId",
      options: kiosks?.kiosksList || [], // List of objects
      optionLabel: "name",
      optionValue: "id",
    },
    {
      label: t("transactionScreen.filterDrawer.transactionTypes"),
      key: "type",
      options: [
        {
          id: "Ticket",
          value: "Ticket",
          name: t("transactionScreen.transactionTypes.ticket"),
        },
        {
          id: "New card",
          value: "New card",
          name: t("transactionScreen.transactionTypes.newCard"),
        },
        {
          id: "Top up card",
          value: "Top up card",
          name: t("transactionScreen.transactionTypes.topupCard"),
        },
        {
          id: "Special card",
          value: "Special card",
          name: t("transactionScreen.transactionTypes.specialCard"),
        },
      ],
      optionLabel: "name",
      optionValue: "id",
    },
    {
      label: t("transactionScreen.filterDrawer.paymentTypes"),
      key: "paymentType",
      options: [
        {
          id: "CASH",
          value: "CASH",
          name: t("transactionScreen.paymentTypes.cash"),
        },
        {
          id: "CARD",
          value: "CARD",
          name: t("transactionScreen.paymentTypes.card"),
        },
      ],
      optionLabel: "name",
      optionValue: "id",
    },
    {
      label: t("transactionScreen.filterDrawer.drivers"),
      key: "driverId",
      options: users?.usersList || [], // TODO: change the list of users to the list of drivers
      optionLabel: "name",
      optionValue: "id",
    },
    {
      label: t("transactionScreen.filterDrawer.paymentStatus"),
      key: "paymentStatus",
      options: [
        { id: "PAID", value: "PAID", name: t("statuses.paid") },
        { id: "NOT_PAID", value: "NOT_PAID", name: t("statuses.notPaid") },
      ],
      optionLabel: "name",
      optionValue: "id",
    },
    // Add more fields as needed
  ];
  const fetchTransactions = useCallback((page = 1, pageSize = 10) => {
    setMakingApiCall(true);
    const start = dateFilter[0]
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

    // Set end to 23:59
    const end = dateFilter[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    const { filterFromKiosk } = location.state || {};
    if (filterFromKiosk) {
      handleSelectDorpdownsChange("type", filterFromKiosk.transactionType);
      handleSelectDorpdownsChange("kioskId", filterFromKiosk?.kiosk);
      location.state = {};
    }
    dispatch(
      getFilteredTransactions({
        start,
        end,
        type: selectedType,
        paymentType: selectedPaymentType,
        kioskId: selectedKiosk,
        driverId: selectedDriver,
        kioskType: selectedKioskType,
        bundleId: selectedBundle,
        page: page,
        pageSize: pageSize,
        search: filter,
        // Remove paymentStatus from backend call - will filter on frontend
      })
    )
      .unwrap()
      .then(() => setMakingApiCall(false))
      .catch((error) => {
        message.error(error.message);
        setMakingApiCall(false);
      });
  }, [
    dateFilter,
    selectedType,
    selectedPaymentType,
    selectedKiosk,
    selectedDriver,
    selectedKioskType,
    selectedBundle,
    dispatch,
    location,
    filter
  ]);
  useEffect(() => {
    fetchTransactions();
  }, [fetchTransactions]);

  useEffect(() => {
    //fetchCards();
    const delayedSearch = debounce(() => {
      fetchTransactions();
    }, 500); // Wait 500ms after the last keystroke

    delayedSearch();
    return delayedSearch.cancel;
  }, [fetchTransactions]);

  // Reset reprinted status when transaction type changes away from TICKET_TYPE
  useEffect(() => {
    if (selectedType !== "TICKET_TYPE" && selectedReprintedStatus !== null) {
      setSelectedReprintedStatus(null);
      setSelectedOptionDropdownFields((prev) => ({
        ...prev,
        reprintedStatus: null,
      }));
    }
  }, [selectedType, selectedReprintedStatus]);
  const filtredData =
    transactions?.data?.filter((item) => {
      // Text search filter
      const matchesTextSearch =
        item?.kioskName?.toLowerCase()?.indexOf(filter.toLowerCase()) !==
          -1 ||
        item?.paymentType.toLowerCase()?.indexOf(filter.toLowerCase()) !== -1 ||
        item.bundleName
          .toString()
          .toLowerCase()
          .indexOf(filter.toLowerCase()) !== -1 ||
        item.type.toString().toLowerCase().indexOf(filter.toLowerCase()) !==
          -1 ||
        formatDate(item.created.toString())
          ?.toLowerCase()
          .indexOf(filter.toLowerCase()) !== -1;

      // Payment status filter
      const matchesPaymentStatus = selectedPaymentStatus
        ? item.paymentStatus === selectedPaymentStatus
        : true; // Reprinted status filter (only for TICKET_TYPE transactions and when reprinted status is selected)
      const matchesReprintedStatus =
        selectedReprintedStatus !== null && selectedType === "TICKET_TYPE"
          ? item.type === "TICKET_TYPE" &&
            Boolean(item.reprinted) === selectedReprintedStatus
          : true;

      return (
        matchesTextSearch && matchesPaymentStatus && matchesReprintedStatus
      );
    }) || [];
  
    const getTicketsByTransaction = async (id) => {
      try {
        const data = await dispatch(getTicketsByTransactionId(id)).unwrap();
        console.log("Tickets:", data);
        return data;
      } catch (err) {
        console.error("Error fetching tickets:", err);
        message.error("Error while fetching tickets");
        return []; // return fallback
      }
    };
    
    const getCardsByTransaction = async (id) => {
      try {
        const data = await dispatch(getCardByTransactionId(id)).unwrap();
        console.log("Cards:", data);
        return data;
      } catch (err) {
        console.error("Error fetching cards:", err);
        message.error("Error while fetching cards");
        return []; // return fallback
      }
    };

    const showModal = async (record, type) => {
      const data = type === "ticket"
        ? await getTicketsByTransaction(record)
        : await getCardsByTransaction(record);
    
      console.log("showModal", data);
      setData(data);
      setType(type);
      setModalOpen(true);
    };

  const closeModal = () => {
    setData([]);
    setType(null);
    setModalOpen(false);
  };

  const showModalSummary = () => {
    if(transactions?.totalTransactions > 0) {
      setMakingApiCall(true);
      const start = dateFilter[0]
        .startOf("day")
        .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

      // Set end to 23:59
      const end = dateFilter[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
      const { filterFromKiosk } = location.state || {};
      if (filterFromKiosk) {
        handleSelectDorpdownsChange("type", filterFromKiosk.transactionType);
        handleSelectDorpdownsChange("kioskId", filterFromKiosk?.kiosk);
        location.state = {};
      }
      dispatch(
        getSummaryTransactions({
          start,
          end,
          type: selectedType,
          paymentType: selectedPaymentType,
          kioskId: selectedKiosk,
          driverId: selectedDriver,
          kioskType: selectedKioskType,
          bundleId: selectedBundle,
          search: filter,
        })
      )
        .unwrap()
        .then((data) => {
          console.log("data", data);
          setTotalSummaryValues(data)
          setMakingApiCall(false)
          setModalSummary(true);
        })
        .catch((error) => {
          message.error(error.message);
          setMakingApiCall(false);
        });
    } else {
      message.warning(t("noData"));
    }
  };

  const closeModalSummary = () => {
    setModalSummary(false);
    setTotalSummaryValues(null)
  };

  const showModalChangeStatus = () => {
    setOpenChangePaymentStatusModal(true);
  };
  const closeModalChangeStatus = () => {
    setOpenChangePaymentStatusModal(false);
    setSelectedTransaction([]);
  };
  const showReprintLogsModal = (transaction) => {
    setSelectedTransactionId(transaction.id);

    setSelectedTransactionForReprint(transaction);
    dispatch(getReprintLogs(transaction.id))
      .unwrap()
      .catch((error) => {
        message.error("Error while fetching reprint logs");
      });
    setIsReprintLogsModalOpen(true);
  };
  const closeReprintLogsModal = () => {
    setIsReprintLogsModalOpen(false);
    setSelectedTransactionId(null);
    setSelectedTransactionForReprint(null);
  };

  const showPaymentDetailsModal = (record) => {
    if (!record) {
      return;
    }

    const paymentDetails = record.payment || record.handheldPayment;
    if (!paymentDetails) {
      return;
    }

    setPaymentDetailsData({ ...record, paymentDetails });
    setPaymentDetailsModalOpen(true);
  };

  const closePaymentDetailsModal = () => {
    setPaymentDetailsData(null);
    setPaymentDetailsModalOpen(false);
  };

  const onFilterByDate = (date, dateString) => {
    const startDate = dayjs(date[0]);
    const endDate = dayjs(date[1]);
    setDateFilter([startDate, endDate]);
  };

  const openFilterDrawer = () => {
    setIsDrawerOpen(true);
  };
  const closeFilterDrawer = () => {
    setIsDrawerOpen(false);
  };

  const onSaveFilter = () => {
    fetchTransactions();
    closeFilterDrawer();
  };

  const handleSelectDorpdownsChange = (key, value) => {
    setSelectedOptionDropdownFields((prev) => ({
      ...prev,
      [key]: value,
    }));
  };
  const createdCsvData = transactions?.data.map((item, index) => {
    return {
      index: index + 1,
      ...item,
      created: formatDateAndTime(item.created),
      driver: item?.driverName
        ? item?.driverName + " " + item?.driverSurname
        : "-",
      paymentStatus:
        item?.paymentStatus === "PAID"
          ? t("statuses.paid")
          : t("statuses.notPaid"),
      paymentStatusUpdatedAt: item?.paymentStatusUpdatedAt
        ? formatDateAndTime(item.paymentStatusUpdatedAt)
        : "-",
      type:
        item.type === "TICKET_TYPE"
          ? t("transactionScreen.transactionTypes.ticket")
          : item.type === "NEW_CARD_TYPE"
          ? t("transactionScreen.transactionTypes.newCard")
          : item.type === "TOP_UP_CARD_TYPE"
          ? t("transactionScreen.transactionTypes.topupCard")
          : item.type === "SPECIAL_CARD_TYPE"
          ? t("transactionScreen.transactionTypes.specialCard")
          : item.type === "CARD_REPLACEMENT_FEE"
          ? t("transactionScreen.transactionTypes.cardReplacement")
          : "-",
    };
  });

  const fetchAllTransactions = async () => {
    setMakingApiCall(true);
    try {
      setMakingApiCall(true);
    const start = dateFilter[0]
      .startOf("day")
      .format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");

    // Set end to 23:59
    const end = dateFilter[1].endOf("day").format("YYYY-MM-DDTHH:mm:ss.SSS[Z]");
    const { filterFromKiosk } = location.state || {};
    if (filterFromKiosk) {
      handleSelectDorpdownsChange("type", filterFromKiosk.transactionType);
      handleSelectDorpdownsChange("kioskId", filterFromKiosk?.kiosk);
      location.state = {};
    }
    const data = await dispatch(
      getFilteredTransactions({
        start,
        end,
        type: selectedType,
        paymentType: selectedPaymentType,
        kioskId: selectedKiosk,
        driverId: selectedDriver,
        kioskType: selectedKioskType,
        page: 1,
        pageSize: transactions?.totalTransactions,
        search: filter,
        // Remove paymentStatus from backend call - will filter on frontend
      })
    ).unwrap();
      setMakingApiCall(false);
      setCsvData(data?.content);
      const finalData = data?.content.map((item, index) => {
        return {
          index: index + 1,
          ...item,
          created: formatDateAndTime(item.created),
          driver: item?.driverName
            ? item?.driverName + " " + item?.driverSurname
            : "-",
          paymentStatus:
            item?.paymentStatus === "PAID"
              ? t("statuses.paid")
              : t("statuses.notPaid"),
          paymentStatusUpdatedAt: item?.paymentStatusUpdatedAt
            ? formatDateAndTime(item.paymentStatusUpdatedAt)
            : "-",
          type:
            item.type === "TICKET_TYPE"
              ? t("transactionScreen.transactionTypes.ticket")
              : item.type === "NEW_CARD_TYPE"
              ? t("transactionScreen.transactionTypes.newCard")
              : item.type === "TOP_UP_CARD_TYPE"
              ? t("transactionScreen.transactionTypes.topupCard")
              : item.type === "SPECIAL_CARD_TYPE"
              ? t("transactionScreen.transactionTypes.specialCard")
              : item.type === "CARD_REPLACEMENT_FEE"
              ? t("transactionScreen.transactionTypes.cardReplacement")
              : "-",
        };
      });
      return finalData; // Return the data to the caller
    } catch (error) {
      setMakingApiCall(false);
      console.error("Error fetching cards", error); // Re-throw to let the caller handle it
    }
  };

  const handleGenerateCsv = (event, done) => {
    const headers = [
      {
        label: "#",
        key: "index",
      },
      {
        label: t("transactionScreen.table.kiosk"),
        key: "kioskName",
      },
      {
        label: t("transactionScreen.table.kioskSerialNumber"),
        key: "kioskSerialNumber",
      },
      {
        label: t("transactionScreen.table.transactionType"),
        key: "type",
      },
      {
        label: t("transactionScreen.table.paymentType"),
        key: "paymentType",
      },
      {
        label: t("transactionScreen.table.totalAmount"),
        key: "totalAmount",
      },
      {
        label: t("transactionScreen.table.bundle"),
        key: "bundleName",
      },
      {
        label: t("transactionScreen.table.tickets"),
        key: "ticketCount",
      },
      {
        label: t("transactionScreen.table.cards"),
        key: "cardCount",
      },
      {
        label: t("transactionScreen.table.date"),
        key: "created",
      },
      {
        label: t("transactionScreen.table.driver"),
        key: "driver",
      },
      {
        label: t("transactionScreen.table.paymentStatus"),
        key: "paymentStatus",
      },
      {
        label: t("transactionScreen.table.paymentStatusUpdatedAt"),
        key: "paymentStatusUpdatedAt",
      },
    ];

    const data = Array.isArray(createdCsvData) ? createdCsvData : [];
    done(data, headers);
  };

  const handleFilter = (values) => {
    onSaveFilter();
  };

  const [selectedTransaction, setSelectedTransaction] = useState([]);

  const onSelectTransactionsChange = (selectedRowKeys) => {
    setSelectedTransaction(selectedRowKeys);
  };

  const rowSelection = {
    type: "checkbox",
    selectedRowKeys: selectedTransaction,
    onChange: onSelectTransactionsChange,
    hideDefaultSelections: true,
    getCheckboxProps: (record) => ({
      //disabled: record.cancelled ? true : false, // Column configuration not to be checked
      name: record.id,
    }),
  };

  return (
    <>
      <Header
        onAddButtonClick={() => {}}
        onSearch={(e) => {
          setFilter(e.target.value);
        }}
        searchPlaceholder={t("transactionScreen.header.search")}
        addButtonTextColor={"#ffffff"}
        onReload={fetchTransactions}
        showAddButton={false}
        BreadcrumbItems={[
          {
            title: t("transactionScreen.header.title"),
          },
        ]}
        showBreadcrumb={true}
        showFilterByDate={true}
        onFilterByDate={(date, dateString) => onFilterByDate(date, dateString)}
        showFilterButton={true}
        onClickFilterButton={() => {
          openFilterDrawer();
        }}
        dateRange={dateFilter}
        showExportCSV={true}
        csvHeaders={csvHeaders}
        csvData={createdCsvData}
        generateCSVfn={handleGenerateCsv}
        fetchDataPdf={fetchAllTransactions}
        csvDataMiddleFileName={"Transactions"}
        showSummaryButton={true}
        onClickSummaryButton={() => {
          showModalSummary();
        }}
        summaryButtonText={t("transactionSummary.title")}
        showChangeStatusButton={true}
        ChangeStatusButtonText={t(
          "transactionScreen.header.updatePaymentStatus"
        )}
        disableChangeStatusButton={selectedTransaction.length === 0}
        onClickChangeStatusButton={() => {
          showModalChangeStatus();
        }}
      />{" "}
      <Card style={{ margin: 16 }}>
        <CustomTableComponents
          isMakingApiCall={isMakingApiCall}
          columns={columns.filter((column) => column.show)}
          dataArray={transactions?.data}
          handleSelectedRow={() => {}}
          handleDoubleClickRow={() => {}}
          rowSelection={rowSelection}
          paginationMeta={transactions?.totalTransactions}
          onPageChange={(page, pageSize) => fetchTransactions(page, pageSize)}
        />
        <TicketCardModal
          isModalOpen={isModalOpen}
          handleCancel={closeModal}
          toBeDisplayed={type}
          data={data}
        />
        {isPaymentDetailsModalOpen && (
          <PaymentDetailsModal
            isModalOpen={isPaymentDetailsModalOpen}
            handleCancel={closePaymentDetailsModal}
            data={paymentDetailsData} // Pass the full record with payment details
          />
        )}
        {isDrawerOpen && (
          <TransactionFilterDrawer
            isDrawerOpen={isDrawerOpen}
            setIsDrawerOpen={setIsDrawerOpen}
            loading={isMakingApiCall} // TOFIX: loading state
            onSave={handleFilter}
            selectedKiosk={selectedKiosk}
            setSelectedKiosk={setSelectedKiosk}
            selectedDriver={selectedDriver}
            setSelectedDriver={setSelectedDriver}
            selectedType={selectedType}
            setSelectedType={setSelectedType}
            selectedPaymentType={selectedPaymentType}
            setSelectedPaymentType={setSelectedPaymentType}
            selectedPaymentStatus={selectedPaymentStatus}
            setSelectedPaymentStatus={setSelectedPaymentStatus}
            selectedReprintedStatus={selectedReprintedStatus}
            setSelectedReprintedStatus={setSelectedReprintedStatus}
            selectedBundle={selectedBundle}
            setSelectedBundle={setSelectedBundle}
            dateRange={dateFilter}
            onDateRangeChange={onDateRangeChange}
            selectedKioskType={selectedKioskType}
            setSelectedKioskType={setSelectedKioskType}
            closeFilteringDrawer={closeFilteringDrawer}
            onResetForm={onResetFilter}
          />
        )}{" "}
        {modalSummary && (
          <SummaryModal
            isModalOpen={modalSummary}
            handleCancel={closeModalSummary}
            selectedDate={dateFilter}
            selectedDriver={selectedDriver}
            selectedKiosk={selectedKiosk}
            totalSummary={totalSummaryValues}
          />
        )}{" "}
        {openChangePaymentStatusModal && (
          <TransactionPaymentStatus
            isModalOpen={openChangePaymentStatusModal}
            handleCancel={closeModalChangeStatus}
            transactionsId={selectedTransaction}
            onRefresh={fetchTransactions}
          />
        )}{" "}
        {isReprintLogsModalOpen && (
          <ReprintLogsModal
            isModalOpen={isReprintLogsModalOpen}
            handleCancel={closeReprintLogsModal}
            reprintLogs={reprintLogs}
            transactionId={selectedTransactionId}
            reprintCount={selectedTransactionForReprint?.reprintCount}
          />
        )}
      </Card>
    </>
  );
};

export default Transaction;
